<script setup lang="ts">
  import { computed } from 'vue';
  import { NumberStepperInput, SelectInput } from '@/shared/ui';
  import { getIndexingFields, getTextFields } from '../../lib/matrix-fields';
  import {
    indexingSelectOptions,
    MAX_INDEXING_FIELDS,
    MIN_INDEXING_SEQUENCE_START,
  } from '../../lib/record-config-options';
  import { rejectNonIntegerKeys, toWholeNumber } from '../../lib/whole-number-input';
  import RecordConfigFieldCombobox from './RecordConfigFieldCombobox.vue';
  import RecordConfigOsOfIndexingBlock from './RecordConfigOsOfIndexingBlock.vue';
  import RecordConfigRepeater from './RecordConfigRepeater.vue';
  import type { MatrixField } from '../../model/matrix-field.types';
  import type { RecordIndexingMode } from '../../model/record-config.types';

  const LABEL_ID = 'record-config-indexing-label';
  const SEQUENCE_START_INPUT_ID = 'record-config-indexing-sequence-start';
  const SEQUENCE_START_LABEL = 'Início da sequência';
  const SEQUENCE_START_INPUT_CLASS =
    'h-12 w-full bg-white-01 text-gray-07 placeholder:text-gray-03 disabled:cursor-not-allowed disabled:border-gray-16 disabled:text-gray-16';
  const FIELD_PLACEHOLDER = 'Selecione um campo';
  const FIELDS_ERROR_MESSAGE = 'Não foi possível carregar os campos do formulário.';
  const LOCKED_MESSAGE = 'A Indexação não muda depois que a revisão gera Registro.';

  const props = withDefaults(
    defineProps<{
      modelValue: RecordIndexingMode | null;
      fieldNames: Array<string | null>;
      sequenceStart: number | null;
      indexerFieldName: string | null;
      fields: MatrixField[];
      isLoadingFields: boolean;
      fieldsFailed: boolean;
      isIfApiType: boolean;
      locked?: boolean;
    }>(),
    { locked: false }
  );

  const emit = defineEmits<{
    'update:modelValue': [value: RecordIndexingMode];
    'update:fieldNames': [value: Array<string | null>];
    'update:sequenceStart': [value: number | null];
    'update:indexerFieldName': [value: string | null];
  }>();

  const selectOptions = computed(() => indexingSelectOptions(props.isIfApiType));
  const indexingFields = computed(() => getIndexingFields(props.fields));
  const textFields = computed(() => getTextFields(props.fields));
  const titleNames = computed(() =>
    Array.from({ length: MAX_INDEXING_FIELDS }, (_, index) => props.fieldNames[index] ?? null)
  );
  const needsFields = computed(
    () => props.modelValue === 'NON_SEQUENTIAL' || props.modelValue === 'OS_OF'
  );

  function onSelect(value: string | number): void {
    const option = selectOptions.value.find((candidate) => candidate.id === value);
    if (!option) return;
    emit('update:modelValue', option.id);
    emit('update:fieldNames', []);
    emit('update:sequenceStart', null);
    emit('update:indexerFieldName', null);
  }

  function setSequenceStart(value: number | null): void {
    emit('update:sequenceStart', toWholeNumber(value, MIN_INDEXING_SEQUENCE_START));
  }

  function optionsFor(index: number, ownValue: string | null): MatrixField[] {
    const takenElsewhere = new Set(
      props.fieldNames.filter((value, rowIndex) => rowIndex !== index && value !== null)
    );

    return indexingFields.value.filter(
      (field) => field.name === ownValue || !takenElsewhere.has(field.name)
    );
  }
</script>

<template>
  <section class="flex flex-col gap-2 px-8 py-6" data-testid="record-config-indexing-section">
    <span :id="LABEL_ID" class="text-sm font-semibold text-gray-07">Indexação:</span>
    <SelectInput
      id="record-config-indexing"
      :model-value="modelValue ?? ''"
      :options="selectOptions"
      label="Selecione"
      :auto-select-first="false"
      :disabled="isIfApiType || locked"
      :aria-labelledby="LABEL_ID"
      container-class="w-full max-w-72"
      @update:model-value="onSelect"
    />

    <p
      v-if="locked"
      class="text-sm text-gray-03"
      data-testid="record-config-indexing-locked"
    >
      {{ LOCKED_MESSAGE }}
    </p>

    <p
      v-if="needsFields && fieldsFailed"
      class="text-sm text-gray-05"
      data-testid="record-config-indexing-fields-error"
    >
      {{ FIELDS_ERROR_MESSAGE }}
    </p>

    <template v-if="modelValue === 'SEQUENTIAL'">
      <label :for="SEQUENCE_START_INPUT_ID" class="mt-2 text-sm font-semibold text-gray-07">
        {{ SEQUENCE_START_LABEL }}:
      </label>
      <NumberStepperInput
        :input-id="SEQUENCE_START_INPUT_ID"
        :model-value="sequenceStart"
        :min="MIN_INDEXING_SEQUENCE_START"
        :step="1"
        :placeholder="SEQUENCE_START_LABEL"
        :aria-label="SEQUENCE_START_LABEL"
        wrapper-class="w-44"
        :input-class="SEQUENCE_START_INPUT_CLASS"
        :disabled="locked"
        @keydown="rejectNonIntegerKeys"
        @update:model-value="setSequenceStart"
      />
    </template>

    <RecordConfigRepeater
      v-else-if="modelValue === 'NON_SEQUENTIAL'"
      :model-value="fieldNames"
      :empty-value="null"
      :max="MAX_INDEXING_FIELDS"
      add-label="Adicionar campo"
      remove-label="Remover campo"
      :disabled="locked"
      @update:model-value="emit('update:fieldNames', $event)"
    >
      <template #default="{ value, index, update }">
        <RecordConfigFieldCombobox
          :model-value="value"
          :options="optionsFor(index, value)"
          :placeholder="FIELD_PLACEHOLDER"
          :is-loading="isLoadingFields"
          :aria-labelledby="LABEL_ID"
          :disabled="locked"
          @update:model-value="update($event)"
        />
      </template>
    </RecordConfigRepeater>

    <RecordConfigOsOfIndexingBlock
      v-else-if="modelValue === 'OS_OF'"
      :indexer-field-name="indexerFieldName"
      :title-names="titleNames"
      :fields="textFields"
      :is-loading="isLoadingFields"
      :disabled="locked"
      @update:indexer-field-name="emit('update:indexerFieldName', $event)"
      @update:title-names="emit('update:fieldNames', $event)"
    />
  </section>
</template>
